import * as React from 'react';
import {
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Select,
  TextField,
  TextFieldProps,
  withStyles
} from '@material-ui/core';
import * as styles from './build-segment.module.scss';
import { Clear } from '@material-ui/icons';
import {
  SegmentAttributeDatetime,
  SelectedAttributeDaterange,
  SelectedAttributeDaysRange
} from '../../../models/segments';
import { MultiselectChip } from './multiselect-chip';
import AttributeFilterDialog from './attribute-filter-dialog';
import moment from 'moment';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { useDatetimePickers } from './useDatetimePickers';
import { number, ValidationError } from 'yup';
import { useBuildSegment } from './useBuildSegment';

interface Props {
  attribute: SegmentAttributeDatetime;
  selectedAttribute: SelectedAttributeDaterange | SelectedAttributeDaysRange;
  position: number[];
  exclude?: boolean;
  applyTransactional?: (options: any) => void;
  deleteTransactionalChip?: (options: any) => void;
  attributeIndex?: number;
}

interface DateRange {
  min: moment.Moment;
  max: moment.Moment;
}

const useStyles = makeStyles({
  datePicker: {
    display: 'block',
    margin: '0 0 10px 30px',
    width: 150
  },
  daysInput: {
    width: 60,
    margin: '0 4px',
    // visually hide the select label
    '& label.MuiFormLabel-root, legend': {
      clip: 'rect(0 0 0 0)',
      clipPath: 'inset(50%)',
      height: 1,
      overflow: 'hidden',
      position: 'absolute',
      whiteSpace: 'nowrap',
      width: 1
    },
    '& .MuiOutlinedInput-notchedOutline': {
      top: 0
    }
  }
});

const DatePickerStyledInput = withStyles({
  root: {
    '& .MuiOutlinedInput-root': {
      fontSize: '14px'
    }
  }
})(TextField);

const DatePickerInput = (props: TextFieldProps) => <DatePickerStyledInput {...props} size="small" variant="outlined" />;

const nDaysSchema = number()
  .integer('Must be an integer')
  .min(1, 'Must be greater than 1')
  .max(365, 'Must be less than 365')
  .typeError('Must be a number');

/**
 * Modal dialog component to render the filter options for a single attribute when building a segment
 */
const AttributeFilterDatetime = ({
  attribute,
  selectedAttribute,
  position,
  exclude,
  applyTransactional,
  attributeIndex
}: Props) => {
  const overrides = useStyles();
  const { dispatch } = useBuildSegment(exclude);
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [rangeType, setRangeType] = React.useState<'custom' | 'n-days'>(
    'numberOfDays' in selectedAttribute ? 'n-days' : 'custom'
  );
  const [nDaysValue, setNDaysValue] = React.useState<string>(
    'numberOfDays' in selectedAttribute ? String(selectedAttribute.numberOfDays) : '30'
  );
  const [nDaysValidationError, setNDaysValidationError] = React.useState<string>('');
  const [selectedRange, setSelectedRange] = React.useState<DateRange | string>(() => {
    if ('dateRange' in selectedAttribute) {
      return {
        min: selectedAttribute.dateRange.min && moment(selectedAttribute.dateRange.min),
        max: selectedAttribute.dateRange.max && moment(selectedAttribute.dateRange.max)
      };
    }
    if ('numberOfDays' in selectedAttribute) {
      return String(selectedAttribute.numberOfDays);
    }
    return { min: null, max: null };
  });
  const {
    minValue,
    setMinValue,
    maxValue,
    setMaxValue,
    isMinValid,
    isMaxValid,
    warning: customRangeWarning,
    onMinValueChange,
    onMaxValueChange
  } = useDatetimePickers(
    'dateRange' in selectedAttribute ? selectedAttribute.dateRange.min : null,
    'dateRange' in selectedAttribute ? selectedAttribute.dateRange.max : null
  );

  React.useEffect(() => {
    if (typeof selectedRange == 'string') {
      setNDaysValue(selectedRange);
      if (applyTransactional) {
        applyTransactional({ rangeType: 'n-days', numberOfDays: parseInt(selectedRange) });
      } else {
        // @ts-ignore -- removing dateRange which may or may not exist, only use remaining properties
        const { dateRange: _, ...rest } = selectedAttribute;
        dispatch({
          type: 'updateAttribute',
          attribute: { ...rest, numberOfDays: parseInt(selectedRange) },
          position,
          attributeIndex: attributeIndex
        });
      }
    } else {
      setMinValue(selectedRange.min);
      setMaxValue(selectedRange.max);
      if (applyTransactional) {
        applyTransactional({ rangeType: 'custom', min: selectedRange.min ?? null, max: selectedRange.max ?? null });
      } else {
        // @ts-ignore -- removing numberOfDays which may or may not exist, only use remaining properties
        const { numberOfDays: _, ...rest } = selectedAttribute;
        dispatch({
          type: 'updateAttribute',
          attribute: {
            ...rest,
            dateRange: {
              min: selectedRange.min && selectedRange.min.toISOString(),
              max: selectedRange.max && selectedRange.max.toISOString()
            }
          },
          position,
          attributeIndex: attributeIndex
        });
      }
    }
  }, [selectedRange]);

  React.useEffect(() => {
    try {
      nDaysSchema.validateSync(nDaysValue);
      setNDaysValidationError('');
    } catch (err) {
      if (err instanceof ValidationError) {
        setNDaysValidationError(err.message);
      }
    }
  }, [nDaysValue]);

  /** Reset to previous filters on cancel */
  const handleCancel = () => {
    if (typeof selectedRange == 'string') {
      setNDaysValue(selectedRange);
      setMinValue(null);
      setMaxValue(null);
    } else {
      setNDaysValue('30');
      setMinValue(selectedRange.min);
      setMaxValue(selectedRange.max);
    }
    setDialogOpen(false);
  };

  const handleApply = () => {
    if (rangeType === 'custom') {
      setSelectedRange({
        min: minValue,
        max: maxValue
      });
    } else {
      setSelectedRange(nDaysValue);
    }
    setDialogOpen(false);
  };

  const handleMinBlur = () => {
    if (minValue != null && minValue.isBefore(attribute.filters.min)) {
      setMinValue(attribute.filters.min);
    }
  };

  const handleMaxBlur = () => {
    if (maxValue != null && maxValue.isAfter(attribute.filters.max)) {
      setMaxValue(attribute.filters.max);
    }
  };

  /** Function passed to filter select `renderValue` prop: conditionally render the custom content of the filter multi-select menu */
  const renderFilterValues = (value: DateRange | string) => {
    if (typeof value == 'string') {
      return (
        <div className={styles.filterChips}>
          <MultiselectChip
            key={`${attribute.id}-min`}
            label={`Last ${value} days`}
            size="small"
            deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="delete filter" />}
            onDelete={() => {
              setRangeType('custom');
              setSelectedRange({ min: null, max: null });
            }}
          />
        </div>
      );
    }
    if (value.max == null && value.min == null) {
      return <span className={styles.filterText}>All - click for additional filtering</span>;
    }
    return (
      <div className={styles.filterChips}>
        {value.min && (
          <MultiselectChip
            key={`${attribute.id}-min`}
            label={`Start: ${value.min.format('MM/DD/YYYY')}`}
            size="small"
            deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="delete filter" />}
            onDelete={() => setSelectedRange((prev) => ({ ...(prev as DateRange), min: null }))}
          />
        )}
        {value.max && (
          <MultiselectChip
            key={`${attribute.id}-max`}
            label={`End: ${value.max.format('MM/DD/YYYY')}`}
            size="small"
            deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="delete filter" />}
            onDelete={() => setSelectedRange((prev) => ({ ...(prev as DateRange), max: null }))}
          />
        )}
      </div>
    );
  };

  function formIsNotValid() {
    if (rangeType == 'custom') {
      return (minValue && !isMinValid) || (maxValue && !isMaxValid) || customRangeWarning != '';
    }
    if (rangeType == 'n-days') {
      return nDaysValidationError !== '';
    }
  }

  return (
    <React.Fragment>
      <Select
        id={`filter-attribute-${attribute.id}`}
        multiple={true}
        value={selectedRange}
        open={false}
        onOpen={() => {
          setDialogOpen(true);
        }}
        renderValue={renderFilterValues}
        displayEmpty={true}
      />
      <AttributeFilterDialog
        open={dialogOpen}
        title="Select Filters"
        applyDisabled={formIsNotValid()}
        handleCancel={handleCancel}
        handleApply={handleApply}
      >
        <h4 className={styles.rangeFilterTitle}>Date Range</h4>
        <RadioGroup
          aria-label={`${attribute.name}-filter-options`}
          name={`${attribute.name}-filter-options`}
          value={rangeType}
          onChange={(event) => setRangeType(event.target.value as 'n-days' | 'custom')}
        >
          <FormControlLabel
            value="n-days"
            control={<Radio />}
            label={
              <span className={styles.dateRangeDaysRadio}>
                Within the last{' '}
                <TextField
                  label="N-days"
                  id={`${attribute.name}-days`}
                  size="small"
                  type="number"
                  variant="outlined"
                  disabled={rangeType !== 'n-days'}
                  className={overrides.daysInput}
                  InputProps={{ inputProps: { min: 1, max: 365 } }}
                  value={nDaysValue}
                  onChange={(event) => setNDaysValue(event.target.value)}
                  error={nDaysValidationError !== ''}
                />
                days
              </span>
            }
          />
          {rangeType === 'n-days' && nDaysValidationError && (
            <p className={styles.dateRangeDaysError}>{nDaysValidationError}</p>
          )}
          <FormControlLabel value="custom" control={<Radio />} label="Custom date range" />
        </RadioGroup>
        <KeyboardDatePicker
          label="Start Date"
          value={minValue}
          onChange={onMinValueChange}
          format="MM/DD/YYYY"
          variant="inline"
          inputVariant="outlined"
          TextFieldComponent={DatePickerInput}
          KeyboardButtonProps={{ size: 'small', edge: 'end' }}
          minDate={attribute.filters.min}
          minDateMessage={
            rangeType === 'custom' && `Earliest available date: ${attribute.filters.min.format('M/D/YYYY')}`
          }
          maxDate={attribute.filters.max}
          maxDateMessage={`Latest available date: ${attribute.filters.max.format('M/D/YYYY')}`}
          onBlur={handleMinBlur}
          className={overrides.datePicker}
          disabled={rangeType !== 'custom'}
        />
        <KeyboardDatePicker
          label="End Date"
          value={maxValue}
          onChange={onMaxValueChange}
          format="MM/DD/YYYY"
          variant="inline"
          inputVariant="outlined"
          TextFieldComponent={DatePickerInput}
          KeyboardButtonProps={{ size: 'small', edge: 'end' }}
          minDate={attribute.filters.min}
          minDateMessage={`Earliest available date: ${attribute.filters.min.format('M/D/YYYY')}`}
          maxDate={attribute.filters.max}
          maxDateMessage={`Latest available date: ${attribute.filters.max.format('M/D/YYYY')}`}
          onBlur={handleMaxBlur}
          className={overrides.datePicker}
          disabled={rangeType !== 'custom'}
        />
        {rangeType === 'custom' && customRangeWarning && (
          <p className={styles.dateRangeWarning}>{customRangeWarning}</p>
        )}
      </AttributeFilterDialog>
    </React.Fragment>
  );
};

export default AttributeFilterDatetime;
